/* Reference PDP purchase bar, connected to Ikahi's existing product form. */
if (!customElements.get('ikahi-pdp-purchase')) {
  customElements.define('ikahi-pdp-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.variants = JSON.parse(this.querySelector('[data-purchase-variants]').textContent);
      this.productInfo = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
      this.querySelector('[data-sticky-quantity-minus]').addEventListener('click', () => this.changeQuantity(-1), options);
      this.querySelector('[data-sticky-quantity-plus]').addEventListener('click', () => this.changeQuantity(1), options);
      this.querySelector('[data-sticky-buy-now]').addEventListener('click', () => this.checkout(), options);
      this.querySelector('[data-sticky-reviews-link]').addEventListener('click', () => {
        document.querySelector('#judgeme_product_reviews, .jdgm-review-widget')?.scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'
        });
      }, options);
      this.productInfo?.addEventListener('change', () => this.update(), options);
      this.productInfo?.addEventListener('input', () => this.update(), options);
      // Dawn publishes this after the fetched variant and quantity rules are installed.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.sectionId) return;
          this.unavailable = !data.variant;
          if (data.variant) {
            this.variants = this.variants.filter((variant) => String(variant.id) !== String(data.variant.id));
            this.variants.push(data.variant);
          }
          this.update();
        });
      }
      // Delegation also supports announcement sections reloaded by the theme editor.
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-pdp-get-offer]');
        if (!button) return;
        event.preventDefault();
        this.checkout(3);
      }, options);
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.unsubscribeVariant?.();
    }

    get form() { return document.getElementById(this.dataset.productFormId); }
    get quantityInput() { return this.form?.elements.namedItem('quantity'); }
    get variant() {
      if (this.unavailable) return null;
      const id = this.form?.elements.namedItem('id')?.value;
      return this.variants.find((variant) => String(variant.id) === String(id));
    }
    get quantity() { return Number(this.quantityInput?.value) || this.localQuantity || this.minimum; }
    get minimum() { return Math.max(Number(this.quantityInput?.min) || this.variant?.quantity_rule?.min || 1, 1); }
    get maximum() { return Number(this.quantityInput?.max) || this.variant?.quantity_rule?.max || Infinity; }
    get increment() { return Math.max(Number(this.quantityInput?.step) || this.variant?.quantity_rule?.increment || 1, 1); }

    setQuantity(value) {
      this.localQuantity = value;
      let input = this.quantityInput;
      // A merchant can remove the quantity-selector block. Keep a form-associated value.
      if (!input && this.form) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        this.form.append(input);
      }
      if (input) {
        input.value = value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.update();
    }

    changeQuantity(direction) {
      this.setQuantity(Math.min(this.maximum, Math.max(this.minimum, this.quantity + direction * this.increment)));
    }

    update() {
      const variant = this.variant;
      const available = Boolean(variant?.available);
      this.querySelector('[data-sticky-quantity-value]').textContent = String(this.quantity).padStart(2, '0');
      this.querySelector('[data-sticky-quantity-minus]').disabled = this.busy || this.quantity <= this.minimum;
      this.querySelector('[data-sticky-quantity-plus]').disabled = this.busy || this.quantity >= this.maximum;
      this.querySelector('[data-sticky-buy-now]').disabled = this.busy || !available;
      this.querySelector('[data-sticky-buy-label]').textContent = this.busy ? 'Loading…' : available ? 'Buy Now' : 'Sold Out';
      document.querySelectorAll('[data-pdp-get-offer]').forEach((button) => {
        button.disabled = this.busy || !available;
        button.setAttribute('aria-busy', String(Boolean(this.busy)));
      });
      if (!variant) return;
      const format = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { maximumFractionDigits: 2 });
      this.querySelector('[data-sticky-current-amount]').textContent = format.format(variant.price / 100);
      const onSale = variant.compare_at_price > variant.price;
      const compare = this.querySelector('[data-sticky-compare-price]');
      compare.textContent = onSale ? new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency: this.dataset.currency, maximumFractionDigits: 2
      }).format(variant.compare_at_price / 100) : '';
      compare.classList.toggle('is-hidden', !onSale);
      const discount = this.querySelector('[data-sticky-discount]');
      discount.textContent = onSale ? `${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}% Off` : '';
      discount.classList.toggle('is-hidden', !onSale);
    }

    showError(message, offer) {
      const error = offer ? document.querySelector('[data-offer-error]') : this.querySelector('[data-purchase-error]');
      if (error) { error.textContent = message; error.hidden = !message; }
    }

    async checkout(offerQuantity) {
      if (this.busy) return;
      const offer = offerQuantity === 3;
      this.showError('', offer);
      const variant = this.variant;
      const quantity = offerQuantity ?? this.quantity;
      if (!this.form || !variant?.available) {
        this.showError('This option is unavailable. Please select an available option.', offer);
        return;
      }
      if (quantity < this.minimum || quantity > this.maximum || (quantity - this.minimum) % this.increment !== 0) {
        this.showError(offer ? 'This option cannot be purchased in a quantity of 3. Please select another option.' : 'Please select an available quantity.', offer);
        return;
      }
      this.setQuantity(quantity);
      const checkout = this.productInfo?.querySelector('.sr-headless-checkout, [data-fastrr-product-trigger]');
      if (checkout && !checkout.disabled && checkout.getAttribute('aria-disabled') !== 'true') {
        // Use the same checkout integration as the main product, with the synced quantity.
        checkout.click();
        return;
      }
      this.busy = true;
      this.update();
      try {
        const data = new FormData(this.form);
        data.set('quantity', String(quantity));
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json' }, body: data
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Unable to add this quantity. Please try again.');
        window.location.assign(`${root}checkout`);
      } catch (error) {
        this.showError(error.message || 'Checkout is unavailable. Please try again.', offer);
        this.busy = false;
        this.update();
      }
    }
  });
}
